import React from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "../../lib/cn";
import { PageContainer } from "../../components/layout/PageContainer";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/Dialog";
import { Field, Input } from "../../ui/Input";
import { toast } from "../../ui/Toast";
import {
  bulkUpdateCompanyStatus,
  createCompany,
  exportCompanies,
  listCompanies,
  updateCompany,
  updateCompanyStatus,
  type Company,
  type CompanyStatus,
} from "../../api/companies";

type SortKey = "name" | "status" | "updatedAt";
type SortDir = "asc" | "desc";
type Density = "comfortable" | "compact";

const INITIAL_COMPANIES: Company[] = [];

function SearchIcon() {
  return (
    <svg
      className="h-4 w-4"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 20 20"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        d="M8.5 14a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11Zm4.4-1.1 3 3"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      className="h-4 w-4"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 20 20"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        d="M10 4.5v11M4.5 10h11"
      />
    </svg>
  );
}

function statusWeight(s: CompanyStatus): number {
  switch (s) {
    case "Active":
      return 3;
    case "Inactive":
    default:
      return 1;
  }
}

function statusBadgeClass(status: CompanyStatus): string {
  switch (status) {
    case "Active":
      return "border-[color:rgba(34,197,94,0.30)] bg-[color:rgba(34,197,94,0.10)]";
    case "Inactive":
    default:
      return "border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface-2)]";
  }
}

function Badge({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "info" | "success" | "warning" | "danger";
  children: React.ReactNode;
}) {
  const c =
    tone === "success"
      ? "border-[color:rgba(34,197,94,0.55)] bg-[color:rgba(34,197,94,0.10)] text-[color:rgba(255,255,255,0.92)]"
      : tone === "warning"
        ? "border-[color:rgba(245,158,11,0.55)] bg-[color:rgba(245,158,11,0.10)] text-[color:rgba(255,255,255,0.92)]"
        : tone === "danger"
          ? "border-[color:rgba(239,68,68,0.55)] bg-[color:rgba(239,68,68,0.10)] text-[color:rgba(255,255,255,0.92)]"
          : tone === "info"
            ? "border-[color:rgba(203,72,226,0.55)] bg-[color:rgba(203,72,226,0.10)] text-[color:rgba(255,255,255,0.92)]"
            : "border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.03)] text-[color:rgba(255,255,255,0.88)]";

  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold",
        c,
      ].join(" ")}
    >
      {children}
    </span>
  );
}

function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] px-3 py-2">
      <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm font-bold text-white">
        {value}
      </div>
      {note ? (
        <div className="mt-0.5 text-[11px] text-[color:rgba(255,255,255,0.65)]">
          {note}
        </div>
      ) : null}
    </div>
  );
}

const addCompanySchema = z.object({
  legalName: z.string().min(2, "Company legal name is required."),
  backOfficeId: z.string().min(2, "Back-office ID is required."),
  taxId: z.string().min(2, "Tax ID (NPWP) is required."),
  legalAddress: z.string().min(5, "Legal address is required."),
  sourceBankCode: z.string().min(2, "Source bank code is required."),
  sourceBankAccount: z
    .string()
    .min(5, "Source bank account is required."),
  perBatchLimitIdr: z.string().min(1, "Per batch limit is required."),
  perTransactionFeeIdr: z
    .string()
    .min(1, "Per transaction fee is required."),
  picFullname: z.string().min(2, "PIC fullname is required."),
  picPhonenumber: z.string().min(6, "PIC phone number is required."),
  picUsername: z.string().min(2, "PIC username is required."),
  reasonDescription: z.string().optional(),
});

type AddCompanyForm = z.infer<typeof addCompanySchema>;

const optionalText = (schema: z.ZodString) =>
  z.preprocess(
    (value) =>
      typeof value === "string" && value.trim() === "" ? undefined : value,
    schema.optional(),
  );

const editCompanySchema = z.object({
  legalName: optionalText(z.string().min(2, "Company legal name is too short.")),
  backOfficeId: optionalText(
    z.string().min(2, "Back-office ID is too short."),
  ),
  taxId: optionalText(z.string().min(2, "Tax ID (NPWP) is too short.")),
  sourceBankCode: optionalText(
    z.string().min(2, "Source bank code is too short."),
  ),
  sourceBankAccount: optionalText(
    z.string().min(5, "Source bank account is too short."),
  ),
  perTransactionFeeIdr: optionalText(
    z.string().min(1, "Fee per transaction is required."),
  ),
  picFullname: optionalText(z.string().min(2, "PIC fullname is too short.")),
  picPhonenumber: optionalText(
    z.string().min(6, "PIC phonenumber is too short."),
  ),
});

type EditCompanyForm = z.infer<typeof editCompanySchema>;

export default function ClientsPage() {
  const [query, setQuery] = React.useState("");
  const [companies, setCompanies] =
    React.useState<Company[]>(INITIAL_COMPANIES);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [editingCompany, setEditingCompany] = React.useState<Company | null>(
    null,
  );

  const [statusFilter, setStatusFilter] = React.useState<"All" | CompanyStatus>(
    "All",
  );
  const [sortKey, setSortKey] = React.useState<SortKey>("updatedAt");
  const [sortDir, setSortDir] = React.useState<SortDir>("desc");
  const [density, setDensity] = React.useState<Density>("comfortable");
  const navigate = useNavigate();

  React.useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    listCompanies()
      .then((data) => {
        if (!mounted) return;
        setCompanies(
          [...data].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
        );
      })
      .catch((err) => {
        if (!mounted) return;
        console.error(err);
        toast.danger("Failed to load companies.", {
          title: "Error",
          durationMs: 2600,
        });
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);
  const [selected, setSelected] = React.useState<Record<string, boolean>>({});

  const stats = React.useMemo(() => {
    const total = companies.length;
    const active = companies.filter((c) => c.status === "Active").length;
    const inactive = companies.filter((c) => c.status === "Inactive").length;
    return { total, active, inactive };
  }, [companies]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = companies;

    if (q) {
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q),
      );
    }

    if (statusFilter !== "All") {
      list = list.filter((c) => c.status === statusFilter);
    }

    const sorted = [...list].sort((a, b) => {
      let cmp = 0;

      if (sortKey === "name") {
        cmp = a.name.localeCompare(b.name);
      } else if (sortKey === "status") {
        cmp = statusWeight(a.status) - statusWeight(b.status);
      } else {
        // updatedAt as YYYY-MM-DD: lexicographic sort works
        cmp = a.updatedAt.localeCompare(b.updatedAt);
      }

      return sortDir === "asc" ? cmp : -cmp;
    });

    return sorted;
  }, [companies, query, statusFilter, sortKey, sortDir]);

  const selectedCount = React.useMemo(() => {
    return filtered.reduce((acc, r) => acc + (selected[r.id] ? 1 : 0), 0);
  }, [filtered, selected]);

  const isAllSelected = React.useMemo(() => {
    if (filtered.length === 0) return false;
    return filtered.every((r) => selected[r.id]);
  }, [filtered, selected]);

  const toggleAll = React.useCallback(() => {
    setSelected((prev) => {
      const next = { ...prev };
      const willSelectAll = !isAllSelected;
      for (const r of filtered) next[r.id] = willSelectAll;
      return next;
    });
  }, [filtered, isAllSelected]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddCompanyForm>({
    resolver: zodResolver(addCompanySchema),
    defaultValues: {
      legalName: "",
      backOfficeId: "",
      taxId: "",
      legalAddress: "",
      sourceBankCode: "",
      sourceBankAccount: "",
      perBatchLimitIdr: "",
      perTransactionFeeIdr: "",
      picFullname: "",
      picPhonenumber: "",
      picUsername: "",
      reasonDescription: "",
    },
  });

  const onAddCompany = handleSubmit(async (formValues) => {
    try {
      const newCompany = await createCompany({
        companyLegalName: formValues.legalName,
        backOfficeId: formValues.backOfficeId,
        taxId: formValues.taxId,
        legalAddress: formValues.legalAddress,
        sourceBankCode: formValues.sourceBankCode,
        sourceBankAccount: formValues.sourceBankAccount,
        perBatchLimitIdr: formValues.perBatchLimitIdr,
        perTransactionFeeIdr: formValues.perTransactionFeeIdr,
        picFullname: formValues.picFullname,
        picPhonenumber: formValues.picPhonenumber,
        picUsername: formValues.picUsername,
        reasonDescription: formValues.reasonDescription,
      });
      const merged: Company = { ...newCompany, status: "Active" };
      setCompanies((prev) => [merged, ...prev]);
      toast.success("Company created.", { title: "Success", durationMs: 2400 });
      setIsDialogOpen(false);
      reset();
    } catch (err) {
      toast.danger("Failed to create company.", { title: "Error" });
      console.error(err);
    }
  });

  const {
    register: registerEdit,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    setValue: setEditValue,
    formState: { errors: editErrors, isSubmitting: isEditSubmitting },
  } = useForm<EditCompanyForm>({
    resolver: zodResolver(editCompanySchema),
    defaultValues: {
      legalName: "",
      backOfficeId: "",
      taxId: "",
      sourceBankCode: "",
      sourceBankAccount: "",
      perTransactionFeeIdr: "",
      picFullname: "",
      picPhonenumber: "",
    },
  });

  const openEditDialog = (company: Company) => {
    setEditingCompany(company);
    setEditValue("legalName", company.name, { shouldDirty: false });
    setEditValue("backOfficeId", "", { shouldDirty: false });
    setEditValue("taxId", "", { shouldDirty: false });
    setEditValue("sourceBankCode", "", { shouldDirty: false });
    setEditValue("sourceBankAccount", "", { shouldDirty: false });
    setEditValue("perTransactionFeeIdr", "", { shouldDirty: false });
    setEditValue("picFullname", "", { shouldDirty: false });
    setEditValue("picPhonenumber", "", { shouldDirty: false });
    setIsEditOpen(true);
  };

  const onEditCompany = handleEditSubmit(async (values) => {
    if (!editingCompany) return;
    const payload = {
      ...(values.legalName ? { companyLegalName: values.legalName } : {}),
      ...(values.backOfficeId ? { backOfficeId: values.backOfficeId } : {}),
      ...(values.taxId ? { taxId: values.taxId } : {}),
      ...(values.sourceBankCode ? { sourceBankCode: values.sourceBankCode } : {}),
      ...(values.sourceBankAccount
        ? { sourceBankAccount: values.sourceBankAccount }
        : {}),
      ...(values.perTransactionFeeIdr
        ? { perTransactionFeeIdr: values.perTransactionFeeIdr }
        : {}),
      ...(values.picFullname ? { picFullname: values.picFullname } : {}),
      ...(values.picPhonenumber
        ? { picPhonenumber: values.picPhonenumber }
        : {}),
    };

    const updated = await updateCompany(editingCompany.id, payload);
    setCompanies((prev) =>
      prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
    );
    toast.success("Company updated.", { title: "Success", durationMs: 2200 });
    setIsEditOpen(false);
    setEditingCompany(null);
    resetEdit();
  });

  const onRowAction = (action: "view" | "edit" | "toggleStatus", c: Company) => {
    if (action === "view") {
      navigate(`/company/${c.id}`);
      return;
    }
    if (action === "edit") {
      openEditDialog(c);
      return;
    }
    const nextStatus = c.status === "Active" ? "Inactive" : "Active";
    updateCompanyStatus(c.id, nextStatus)
      .then((updated) => {
        setCompanies((prev) =>
          prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
        );
        toast.success(
          nextStatus === "Active"
            ? "Company activated."
            : "Company deactivated.",
          {
            title: c.name,
            durationMs: 2000,
          },
        );
      })
      .catch(() => {
        toast.danger("Failed to update status.", { title: c.name });
      });
  };

  const onBulkAction = (action: "export" | "setActive" | "setInactive") => {
    if (selectedCount === 0) {
      toast.warning("Select at least one company.", {
        title: "Bulk actions",
        durationMs: 2400,
      });
      return;
    }

    if (action === "export") {
      exportCompanies(Object.keys(selected).filter((id) => selected[id]))
        .then((res) => {
          toast.success("Export ready.", {
            title: "Bulk actions",
            durationMs: 2400,
          });
          if (res?.url) {
            window.open(res.url, "_blank", "noopener,noreferrer");
          }
        })
        .catch(() => {
          toast.danger("Export failed.", { title: "Bulk actions" });
        });
      return;
    }

    const ids = Object.keys(selected).filter((id) => selected[id]);
    const status = action === "setActive" ? "Active" : "Inactive";
    bulkUpdateCompanyStatus(ids, status)
      .then(() => {
        setCompanies((prev) =>
          prev.map((c) =>
            ids.includes(c.id) ? { ...c, status, updatedAt: c.updatedAt } : c,
          ),
        );
        toast.success("Updated selected companies.", {
          title: "Bulk actions",
          durationMs: 2400,
        });
      })
      .catch(() => {
        toast.danger("Bulk update failed.", { title: "Bulk actions" });
      });
  };

  const rowPadY = density === "compact" ? "py-2" : "py-3";

  return (
    <PageContainer
      title="Clients"
      description="Search, filter, and manage client companies."
      actions={
        <>
          <div className="w-full sm:w-[320px]">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or ID…"
              leftIcon={<SearchIcon />}
              aria-label="Search companies"
            />
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="primary" leftIcon={<PlusIcon />}>
                Add Company
              </Button>
            </DialogTrigger>

            <DialogContent wide className="kkp-flowbite">
              <DialogHeader className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
                <div className="grid gap-1">
                  <DialogTitle>Add Company</DialogTitle>
                  <DialogDescription>
                    Create a company record. Validation follows the blueprint.
                  </DialogDescription>
                </div>
              </DialogHeader>

              <form
                className="grid gap-5 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(24,19,36,0.35)] p-4 sm:p-5"
                onSubmit={onAddCompany}
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Company legal name"
                    required
                    error={errors.legalName?.message}
                    hint="Example: PT KamuKirim - Payroll"
                  >
                    <Input
                      placeholder="Acme Corp"
                      autoComplete="organization"
                      {...register("legalName")}
                    />
                  </Field>

                  <Field
                    label="Back-office ID"
                    required
                    error={errors.backOfficeId?.message}
                  >
                    <Input placeholder="BO-123456" {...register("backOfficeId")} />
                  </Field>

                  <Field label="Tax ID (NPWP)" required error={errors.taxId?.message}>
                    <Input placeholder="01.234.567.8-901.000" {...register("taxId")} />
                  </Field>

                  <Field
                    label="Legal address"
                    required
                    error={errors.legalAddress?.message}
                    className="sm:col-span-2"
                  >
                    <Input
                      placeholder="Jl. Sudirman No. 1, Jakarta"
                      {...register("legalAddress")}
                    />
                  </Field>

                  <Field
                    label="Source bank code"
                    required
                    error={errors.sourceBankCode?.message}
                  >
                    <Input placeholder="002" {...register("sourceBankCode")} />
                  </Field>

                  <Field
                    label="Source bank account"
                    required
                    error={errors.sourceBankAccount?.message}
                  >
                    <Input
                      placeholder="1234567890"
                      {...register("sourceBankAccount")}
                    />
                  </Field>

                  <Field
                    label="Per batch limit (IDR)"
                    required
                    error={errors.perBatchLimitIdr?.message}
                  >
                    <Input
                      placeholder="100000000"
                      inputMode="numeric"
                      {...register("perBatchLimitIdr")}
                    />
                  </Field>

                  <Field
                    label="Per transaction fee (IDR)"
                    required
                    error={errors.perTransactionFeeIdr?.message}
                  >
                    <Input
                      placeholder="2500"
                      inputMode="numeric"
                      {...register("perTransactionFeeIdr")}
                    />
                  </Field>

                  <Field
                    label="PIC fullname"
                    required
                    error={errors.picFullname?.message}
                  >
                    <Input
                      placeholder="Rama Wirawan"
                      {...register("picFullname")}
                    />
                  </Field>

                  <Field
                    label="PIC phonenumber"
                    required
                    error={errors.picPhonenumber?.message}
                  >
                    <Input
                      placeholder="+62 812 0000 1234"
                      inputMode="tel"
                      {...register("picPhonenumber")}
                    />
                  </Field>

                  <Field
                    label="PIC username"
                    required
                    error={errors.picUsername?.message}
                  >
                    <Input placeholder="pic.username" {...register("picUsername")} />
                  </Field>

                  <Field
                    label="Reason/Description"
                    error={errors.reasonDescription?.message}
                    className="sm:col-span-2"
                  >
                    <textarea
                      rows={3}
                      placeholder="Reason for adding this company"
                      className="w-full rounded-xl border border-[color:rgba(255,255,255,0.12)] bg-[color:rgba(255,255,255,0.06)] px-3 py-2.5 text-[color:rgba(255,255,255,0.92)] placeholder:text-[color:rgba(255,255,255,0.35)] outline-none transition focus:border-[color:color-mix(in_oklab,var(--kkp-magenta)_70%,white)] focus:shadow-[0_0_0_3px_rgba(203,72,226,0.18)]"
                      {...register("reasonDescription")}
                    />
                  </Field>
                </div>

                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="ghost" type="button">
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Creating…" : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
            <DialogContent wide className="kkp-flowbite">
              <DialogHeader className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
                <div className="grid gap-1">
                  <DialogTitle>Edit Company</DialogTitle>
                  <DialogDescription>
                    Update company details or reset the PIC password.
                  </DialogDescription>
                </div>
              </DialogHeader>

              <form
                className="grid gap-5 rounded-[var(--kkp-radius)] border border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(24,19,36,0.35)] p-4 sm:p-5"
                onSubmit={onEditCompany}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    type="button"
                    onClick={() =>
                      toast.info("PIC password reset requested.", {
                        title: editingCompany?.name ?? "Company",
                        durationMs: 2000,
                      })
                    }
                  >
                    Reset PIC Password
                  </Button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Company legal name"
                    error={editErrors.legalName?.message}
                  >
                    <Input
                      placeholder="Company legal name"
                      {...registerEdit("legalName")}
                    />
                  </Field>

                  <Field
                    label="Back-office ID"
                    error={editErrors.backOfficeId?.message}
                  >
                    <Input
                      placeholder="Back-office ID"
                      {...registerEdit("backOfficeId")}
                    />
                  </Field>

                  <Field
                    label="Tax ID (NPWP)"
                    error={editErrors.taxId?.message}
                  >
                    <Input placeholder="NPWP" {...registerEdit("taxId")} />
                  </Field>

                  <Field
                    label="Fee per transaction (IDR)"
                    error={editErrors.perTransactionFeeIdr?.message}
                  >
                    <Input
                      placeholder="2500"
                      inputMode="numeric"
                      {...registerEdit("perTransactionFeeIdr")}
                    />
                  </Field>

                  <Field
                    label="Source bank code"
                    error={editErrors.sourceBankCode?.message}
                  >
                    <Input
                      placeholder="002"
                      {...registerEdit("sourceBankCode")}
                    />
                  </Field>

                  <Field
                    label="Source bank account"
                    error={editErrors.sourceBankAccount?.message}
                  >
                    <Input
                      placeholder="1234567890"
                      {...registerEdit("sourceBankAccount")}
                    />
                  </Field>

                  <Field
                    label="PIC fullname"
                    error={editErrors.picFullname?.message}
                  >
                    <Input
                      placeholder="PIC fullname"
                      {...registerEdit("picFullname")}
                    />
                  </Field>

                  <Field
                    label="PIC phonenumber"
                    error={editErrors.picPhonenumber?.message}
                  >
                    <Input
                      placeholder="+62 812 0000 1234"
                      inputMode="tel"
                      {...registerEdit("picPhonenumber")}
                    />
                  </Field>
                </div>

                <DialogFooter>
                  <DialogClose asChild>
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={() => {
                        setIsEditOpen(false);
                        setEditingCompany(null);
                        resetEdit();
                      }}
                    >
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button variant="primary" type="submit" disabled={isEditSubmitting}>
                    {isEditSubmitting ? "Saving…" : "Save changes"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </>
      }
    >
      <div className="grid gap-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label="Total companies" value={`${stats.total}`} />
          <Stat label="Active" value={`${stats.active}`} note="Operational" />
          <Stat label="Inactive" value={`${stats.inactive}`} note="Disabled" />
        </div>

        <Card className="p-0 overflow-hidden">
          <div className="border-b border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.02)] px-4 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-xs font-semibold text-[color:rgba(255,255,255,0.9)]">
                  Filters
                </div>

                <div className="flex flex-wrap gap-2">
                  {(["All", "Active", "Inactive"] as const).map(
                    (s) => {
                      const active = statusFilter === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          className={[
                            "inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-bold transition",
                            active
                              ? "border-[color:rgba(255,255,255,0.18)] bg-[color:rgba(255,255,255,0.06)] text-white"
                              : "border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.02)] text-[color:var(--kkp-text-muted)] hover:text-white hover:bg-[color:rgba(255,255,255,0.05)]",
                          ].join(" ")}
                          onClick={() => setStatusFilter(s)}
                          aria-pressed={active}
                        >
                          {s}
                        </button>
                      );
                    },
                  )}
                </div>

                <div className="h-5 w-px bg-[color:rgba(255,255,255,0.10)]" />

                <div className="text-xs font-semibold text-[color:rgba(255,255,255,0.9)]">
                  Sort
                </div>

                <div className="flex flex-wrap gap-2">
                  {(
                    [
                      { key: "updatedAt", label: "Updated" },
                      { key: "name", label: "Name" },
                      { key: "status", label: "Status" },
                    ] as const
                  ).map((s) => {
                    const active = sortKey === s.key;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        className={[
                          "inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-bold transition",
                          active
                            ? "border-[color:rgba(255,255,255,0.18)] bg-[color:rgba(255,255,255,0.06)] text-white"
                            : "border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.02)] text-[color:var(--kkp-text-muted)] hover:text-white hover:bg-[color:rgba(255,255,255,0.05)]",
                        ].join(" ")}
                        onClick={() => {
                          if (sortKey !== s.key) {
                            setSortKey(s.key);
                            setSortDir("asc");
                          } else {
                            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                          }
                        }}
                      >
                        {s.label}
                        {active ? (
                          <span className="ml-1 text-[11px] text-[color:rgba(255,255,255,0.65)]">
                            {sortDir.toUpperCase()}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="text-xs font-semibold text-[color:rgba(255,255,255,0.9)]">
                  Density
                </div>
                <div className="flex flex-wrap gap-2">
                  {(["comfortable", "compact"] as const).map((d) => {
                    const active = density === d;
                    return (
                      <button
                        key={d}
                        type="button"
                        className={[
                          "inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-bold transition",
                          active
                            ? "border-[color:rgba(255,255,255,0.18)] bg-[color:rgba(255,255,255,0.06)] text-white"
                            : "border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.02)] text-[color:var(--kkp-text-muted)] hover:text-white hover:bg-[color:rgba(255,255,255,0.05)]",
                        ].join(" ")}
                        onClick={() => setDensity(d)}
                      >
                        {d === "comfortable" ? "Comfortable" : "Compact"}
                      </button>
                    );
                  })}
                </div>

                <div className="h-5 w-px bg-[color:rgba(255,255,255,0.10)]" />

                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setQuery("");
                    setStatusFilter("All");
                    setSortKey("updatedAt");
                    setSortDir("desc");
                    toast.info("Filters reset.", {
                      title: "Filters",
                      durationMs: 1800,
                    });
                  }}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>

          {selectedCount > 0 ? (
            <div className="border-b border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.02)] px-4 py-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm font-semibold text-white">
                  {selectedCount} selected
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onBulkAction("export")}
                  >
                    Export CSV
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onBulkAction("setActive")}
                  >
                    Set Active
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onBulkAction("setInactive")}
                  >
                    Set Inactive
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelected({})}
                  >
                    Clear selection
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[color:var(--kkp-surface-2)]">
                <tr className="text-[11px] font-bold uppercase tracking-wide text-[color:rgba(255,255,255,0.75)]">
                  <th className="w-[44px] px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleAll}
                      className="h-4 w-4 accent-[color:var(--kkp-magenta)]"
                      aria-label="Select all companies"
                    />
                  </th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Updated</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-sm text-[color:var(--kkp-text-muted)]"
                    >
                      Loading companies…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-sm text-[color:var(--kkp-text-muted)]"
                    >
                      No companies match your filters.
                      <div className="mt-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setQuery("");
                            setStatusFilter("All");
                          }}
                        >
                          Clear filters
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => (
                    <tr
                      key={row.id}
                      className="border-t border-[color:rgba(255,255,255,0.08)]"
                    >
                      <td className={["px-4", rowPadY].join(" ")}>
                        <input
                          type="checkbox"
                          checked={Boolean(selected[row.id])}
                          onChange={(e) =>
                            setSelected((prev) => ({
                              ...prev,
                              [row.id]: e.target.checked,
                            }))
                          }
                          className="h-4 w-4 accent-[color:var(--kkp-magenta)]"
                          aria-label={`Select ${row.name}`}
                        />
                      </td>

                      <td className={["px-4", rowPadY].join(" ")}>
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-[color:rgba(255,255,255,0.92)]">
                            {row.name}
                          </div>
                          <div className="mt-0.5 text-xs text-[color:var(--kkp-text-muted)]">
                            ID: {row.id}
                          </div>
                        </div>
                      </td>

                      <td className={["px-4", rowPadY].join(" ")}>
                        <span
                          className={[
                            "inline-flex items-center rounded-full border px-2.5 py-1",
                            "text-xs font-semibold text-white",
                            statusBadgeClass(row.status),
                          ].join(" ")}
                        >
                          {row.status}
                        </span>
                      </td>

                      <td
                        className={[
                          "px-4",
                          rowPadY,
                          "text-[color:var(--kkp-text-muted)]",
                        ].join(" ")}
                      >
                        {row.updatedAt}
                      </td>

                      <td className={["px-4", rowPadY].join(" ")}>
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => onRowAction("view", row)}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onRowAction("edit", row)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant={row.status === "Active" ? "danger" : "success"}
                            onClick={() => onRowAction("toggleStatus", row)}
                          >
                            {row.status === "Active" ? "Deactivate" : "Activate"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </PageContainer>
  );
}
